import axios from "axios";
import { DataFormat } from "utils/format/DataFormat";
import ModelConverter from "../../utils/modelConverter";
import { BaseApiResponseModel } from "../baseApiResponseModel/BaseApiResponseModel";
import IApiClient from "./IApiClient";

const api = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL!,
  timeout: 90000,
});

// Add a request interceptor
api.interceptors.request.use(
  (config) => {
    const token = DataFormat.getToken();
    const request = config;

    if (token) {
      request.headers = {
        ...(request.headers || {}),
        Authorization: `Bearer ${token}`,
      } as any;
    }

    //for ngrok
    if (
      request?.url?.includes("ngrok-free.app") ||
      request?.url?.includes("ngrok-free.dev")
    ) {
      request.headers!["ngrok-skip-browser-warning"] = "69420";
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Add a response interceptor
api.interceptors.response.use(
  (response) => {
    // Return JSON data
    if (response.data) {
      return response.data;
    }
    return response;
  },
  (error) => {
    const token = DataFormat.getToken();
    const err = error.response || error;
    if (error?.response?.status === 403) {
      if (!!token) {
        sessionStorage.clear();
        localStorage.clear();
        window.location.href = "/login"; //relative to domain
      }
    }
    return Promise.reject(err.data); // Propagate rejection back to caller
  }
);

class AxiosClient implements IApiClient {
  async post<T extends Object>(
    path: string,
    data: Map<string, any> | any = {},
    config?: Map<string, any> | any
  ): Promise<BaseApiResponseModel<T>> {
    let response = await api.post(path, data, config);
    return ModelConverter.decode(response, BaseApiResponseModel<T>);
  }

  async get<T extends Object>(
    path: string,
    data: Map<string, any> | any = {}
  ): Promise<BaseApiResponseModel<T>> {
    let response = await api.get(path, {
      params: data,
    });
    return ModelConverter.decode(response, BaseApiResponseModel<T>);
  }

  async getBlob(
    path: string,
    data: Map<string, any> | any = {},
    config?: Map<string, any> | any
  ): Promise<Blob> {
    const response = await api.get(path, {
      params: data,
      responseType: "blob",
      headers: {
        "Content-Type": "application/xml",
        ...(config?.headers || {}),
      },
    } as any);
    return response as unknown as Blob;
  }

  async delete<T extends Object>(
    path: string,
    data: Map<string, any> | any = {}
  ): Promise<BaseApiResponseModel<T>> {
    let response = await api.delete(path, {
      params: data,
    });
    return ModelConverter.decode(response, BaseApiResponseModel<T>);
  }

  async put<T extends Object>(
    path: string,
    data: Map<string, any> | any = {},
    config?: Map<string, any> | any
  ): Promise<BaseApiResponseModel<T>> {
    let response = await api.put(path, data, config);
    return ModelConverter.decode(response, BaseApiResponseModel<T>);
  }
}

export default new AxiosClient();
